import { useEffect, useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { ChartCard, EmptyState } from '@/components/ChartCard';
import { Skeleton } from '@/components/ui/skeleton';
import { PROVIDERS } from '@/constants/providers';
import { formatNumber, formatPct } from '@/lib/format';
import { ResponsiveContainer, Treemap, Tooltip } from 'recharts';
import { Info } from 'lucide-react';

// Rank -> color: lower (better) = green, higher = red. null = neutral gray.
function rankCellStyle(rank) {
  if (rank === null || rank === undefined) {
    return { bg: 'hsl(210 24% 95%)', fg: 'hsl(215 16% 55%)', label: '—' };
  }
  // Anchors: 1 = deep green, 3 = light green, 5 = amber, 7 = orange, 9+ = red
  let bg;
  let fg = 'hsl(222 47% 15%)';
  if (rank <= 2) { bg = 'hsl(152 60% 82%)'; }
  else if (rank <= 3) { bg = 'hsl(152 55% 88%)'; }
  else if (rank <= 4) { bg = 'hsl(60 90% 88%)'; }
  else if (rank <= 5) { bg = 'hsl(38 92% 85%)'; }
  else if (rank <= 7) { bg = 'hsl(20 90% 84%)'; fg = 'hsl(20 60% 25%)'; }
  else { bg = 'hsl(0 78% 84%)'; fg = 'hsl(0 60% 25%)'; }
  return { bg, fg, label: `#${rank.toFixed(1)}` };
}

// Treemap cell color by visibility_class
const VIS_COLORS = {
  high:   'hsl(152 55% 45%)',
  medium: 'hsl(210 12% 70%)',
  low:    'hsl(0 65% 62%)',
};

function TreemapContent(props) {
  // Recharts Treemap spreads each node's data fields onto props directly.
  const { x, y, width, height, name } = props;
  const visClass = props.visibility_class || 'low';
  const score = typeof props.visibility_score === 'number' ? props.visibility_score : 0;
  const color = VIS_COLORS[visClass] || 'hsl(210 12% 78%)';
  const showLabel = width > 60 && height > 34;
  const showPct = width > 90 && height > 60;
  const pct = (score * 100).toFixed(1) + '%';
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx={6} ry={6} style={{ fill: color, stroke: 'white', strokeWidth: 2 }} />
      {showLabel && (
        <text x={x + width / 2} y={y + height / 2 - (showPct ? 8 : 0)} textAnchor="middle" fill="white" fontSize={13} fontWeight={600} fontFamily="'Space Grotesk',Inter,sans-serif">
          {name}
        </text>
      )}
      {showPct && (
        <text x={x + width / 2} y={y + height / 2 + 12} textAnchor="middle" fill="white" fontSize={13} fontWeight={600} fontFamily="'Space Grotesk',Inter,sans-serif">
          {pct}
        </text>
      )}
    </g>
  );
}

function TreemapTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload || {};
  return (
    <div className="rounded-md border bg-card p-2 text-xs shadow-md">
      <div className="font-medium">{d.name || d.topic}</div>
      <div className="text-muted-foreground mt-0.5">
        {formatNumber(d.mentioned_answers)} of {formatNumber(d.total_answers)} answers mention brand
      </div>
      <div className="text-muted-foreground">Visibility: {formatPct(d.visibility_score)} ({d.visibility_class})</div>
    </div>
  );
}

export default function AEOInsightsTab({ project }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/projects/${project.id}/analytics/topics`);
        setData(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [project.id]);

  const treemapData = useMemo(() => {
    if (!data) return [];
    // Recharts Treemap expects [{name, size, ...extras}]
    return (data.distribution || [])
      .filter((d) => d.prompt_count > 0)
      .map((d) => ({
        name: d.topic,
        size: d.prompt_count,
        ...d,
      }));
  }, [data]);

  if (loading) {
    return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-72" />)}</div>;
  }

  const providers = data?.providers || [];
  const rankings = data?.rankings || [];
  const hasData = rankings.some((r) =>
    providers.some((p) => (r.per_provider?.[p]?.avg_rank ?? null) !== null),
  );

  if (!hasData && treemapData.length === 0) {
    return (
      <ChartCard title="AEO Insights" subtitle="Topic-level rankings and visibility across AI engines">
        <EmptyState
          title="No topic data yet"
          description="Add prompts with categories (e.g. comparison, best_of, how_to) and run at least one scan. Each prompt category becomes a Topic here."
        />
      </ChartCard>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <ChartCard
        title="Topic Position Rankings"
        subtitle="Average rank by topic across AI engines"
        testId="aeo-topic-rankings-card"
        actions={
          <div className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" /> Lower is better
          </div>
        }
      >
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid="aeo-rankings-table">
            <thead>
              <tr className="text-left">
                <th className="px-2 pb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Topic</th>
                {providers.map((p) => {
                  const meta = PROVIDERS[p] || { label: p, dot: 'bg-slate-400' };
                  return (
                    <th key={p} className="px-2 pb-3 text-center">
                      <div className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium">
                        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                        {meta.short || meta.label}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rankings.map((row) => (
                <tr key={row.topic} data-testid={`aeo-rankings-row-${row.topic}`} className="border-t">
                  <td className="px-2 py-2 font-medium capitalize">{row.topic.replace(/_/g, ' ')}</td>
                  {providers.map((p) => {
                    const cell = row.per_provider?.[p] || {};
                    const style = rankCellStyle(cell.avg_rank);
                    return (
                      <td key={p} className="px-2 py-2">
                        <div
                          className="mx-auto flex h-9 w-16 items-center justify-center rounded-md tabular text-sm font-semibold"
                          style={{ background: style.bg, color: style.fg }}
                          title={cell.samples ? `Based on ${cell.samples} answer(s)` : 'No mention yet'}
                        >
                          {style.label}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Worse</span>
            <div className="flex gap-0.5">
              {[9, 6, 4.5, 3.5, 2.5, 1].map((r, i) => {
                const s = rankCellStyle(r);
                return <span key={i} className="h-2.5 w-6 rounded-sm" style={{ background: s.bg }} />;
              })}
            </div>
            <span>Better</span>
          </div>
          <div>{rankings.length} topics · Lower is better</div>
        </div>
      </ChartCard>

      <ChartCard
        title="Topic Distribution"
        subtitle="Sized by prompts, colored by visibility"
        testId="aeo-topic-distribution-card"
      >
        {treemapData.length === 0 ? (
          <EmptyState title="No topic distribution yet" description="Run a scan to populate this treemap." />
        ) : (
          <>
            <div className="h-[380px]">
              <ResponsiveContainer width="100%" height="100%">
                <Treemap
                  data={treemapData}
                  dataKey="size"
                  aspectRatio={4 / 3}
                  stroke="white"
                  content={<TreemapContent />}
                  isAnimationActive={false}
                >
                  <Tooltip content={<TreemapTooltip />} />
                </Treemap>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
              <div className="flex items-center gap-3">
                <LegendDot color={VIS_COLORS.high} label="High visibility" />
                <LegendDot color={VIS_COLORS.medium} label="Medium" />
                <LegendDot color={VIS_COLORS.low} label="Low visibility" />
              </div>
              <div>{treemapData.length} topics</div>
            </div>
          </>
        )}
      </ChartCard>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}
